import React, { useState, useEffect } from 'react';
import { getApiUrl, apiRequest } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const TeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    startDate: '',
    endDate: '',
    periodType: 'custom'
  });

  useEffect(() => {
    fetchTeamMembers();

    // Refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchTeamMembers();
      }
    };

    // Periodic refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchTeamMembers();
    }, 30000);

    // Listen for storage events (cross-tab synchronization)
    const handleStorageChange = (e) => {
      if (e.key === 'team_members_updated') {
        fetchTeamMembers();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await apiRequest(`/team-members`);
      const data = await response.json();
      if (data.success) {
        setTeamMembers(data.team_members);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const { isAdmin } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAdmin()) {
      alert('Only admins can add team members.');
      return;
    }
    
    // Validate required fields
    if (!formData.name || !formData.name.trim()) {
      alert('Error: Team member name is required');
      return;
    }
    
    try {
      const response = await apiRequest(`/team-members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email || null,
          role: formData.role || null
        }),
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setFormData({ name: '', email: '', role: '', startDate: '', endDate: '', periodType: 'custom' });
        setShowForm(false);
        fetchTeamMembers();
        // Notify other tabs about the update
        localStorage.setItem('team_members_updated', Date.now().toString());
        localStorage.removeItem('team_members_updated');
        alert('Team member created successfully!');
      } else {
        // Show detailed error message
        const errorMsg = result.detail || result.message || result.error || 'Failed to create team member';
        alert(`Error: ${errorMsg}`);
        console.error('Team member creation error:', result);
      }
    } catch (error) {
      console.error('Error creating team member:', error);
      alert(`Error: ${error.message || 'Failed to create team member. Please check your connection and try again.'}`);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin()) {
      alert('Only admins can remove team members.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        const response = await apiRequest(`/team-members/${id}`, {
          method: 'DELETE',
        });

        const result = await response.json();
        if (result.success) {
          fetchTeamMembers();
          // Notify other tabs about the update
          localStorage.setItem('team_members_updated', Date.now().toString());
          localStorage.removeItem('team_members_updated');
        } else {
          alert('Error: ' + result.error);
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error deleting team member');
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };


  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {isAdmin() ? 'Team Management' : 'Team Directory'}
          </h2>
          <p className="text-gray-600">
            {isAdmin() ? 'Manage your team members' : 'View your team members'}
          </p>
          {!isAdmin() && (
            <p className="text-sm text-amber-600 mt-1">
              View only. Please contact an admin to add or remove team members.
            </p>
          )}
        </div>
        {isAdmin() && (
          <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowForm(!showForm)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Team Member
            </button>
          </div>
        )}
      </div>

      {/* Add Team Member Form */}
      {isAdmin() && showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Team Member</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  placeholder="Enter role"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Time Period Section */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-md font-medium text-gray-900 mb-4">Time Period</h4>
              
              {/* Period Type Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">Period Type</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="periodType"
                      value="daily"
                      checked={formData.periodType === 'daily'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Daily</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="periodType"
                      value="weekly"
                      checked={formData.periodType === 'weekly'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Weekly</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="periodType"
                      value="monthly"
                      checked={formData.periodType === 'monthly'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Monthly</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="periodType"
                      value="custom"
                      checked={formData.periodType === 'custom'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Custom</span>
                  </label>
                </div>
              </div>

              {/* Date Range (only show for custom period) */}
              {formData.periodType === 'custom' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Add Member
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Team Members List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Team Members ({teamMembers.length})
          </h3>
        </div>
        
        {teamMembers.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
            <p className="text-gray-500">Add your first team member to get started.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {teamMembers.map((member) => (
              <div key={member.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-medium text-sm">
                      {member.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{member.name}</div>
                    <div className="text-sm text-gray-500">
                      {member.email && `${member.email} • `}
                      {member.role || 'No role specified'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                        {member.company ? member.company.charAt(0).toUpperCase() + member.company.slice(1) : 'N/A'}
                      </span>
                      {member.periodType && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                          {member.periodType.charAt(0).toUpperCase() + member.periodType.slice(1)}
                        </span>
                      )}
                      {(member.startDate || member.endDate) && member.periodType === 'custom' && (
                        <span>
                          {member.startDate && `From: ${new Date(member.startDate).toLocaleDateString()}`}
                          {member.startDate && member.endDate && ' • '}
                          {member.endDate && `To: ${new Date(member.endDate).toLocaleDateString()}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {isAdmin() && (
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="text-red-600 hover:text-red-800 p-2 rounded-md hover:bg-red-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;